import { z } from "zod";
import { ehFonteDeTexto } from "./fontes-de-texto.js";
/**
 * As camadas de uma célula da folha: figurinhas, logos e textos que o operador arrasta sobre a foto.
 *
 * Tudo em **frações da célula**, e não em pixels: a tela edita numa prévia pequena e o servidor monta em 300 dpi, e
 * só a fração vale para os dois. O centro da camada é `(x, y)` (% da largura e da altura da célula) e ela gira em
 * torno dele. Valores um pouco fora de 0–100 são válidos: a camada pode passar da borda da célula e o servidor a corta.
 *
 * **Texto: corpo e quebras explícitas.** A camada de texto guarda o `corpo` (o tamanho da letra, em % da largura da
 * célula) e as quebras de linha como `\n`, e NÃO uma caixa com quebra automática: a quebra automática só concorda
 * entre o navegador e o servidor por sorte, e o texto impresso diferente do que estava na tela é o defeito que este
 * desenho existe para evitar. O servidor mede cada linha com os avanços reais da fonte e o navegador usa o mesmo
 * arquivo de fonte (servido pelo próprio servidor, `/api/fontes/…`).
 */
export const MAX_CAMADAS_POR_CELULA = 12;
/** Linhas e tamanho de cada linha de um texto: títulos e frases curtos, que cabem numa foto 3 × 4. */
export const MAX_LINHAS_DO_TEXTO = 4;
export const MAX_CARACTERES_POR_LINHA = 40;
export const ALINHAMENTOS_DO_TEXTO = ["esquerda", "centro", "direita"];
const cor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Cor no formato #rrggbb");
const baseDaCamada = {
    /** Identifica a camada na tela (lista, seleção, ordem); o servidor só a repassa. */
    id: z.string().min(1).max(40),
    /** O centro, em % da largura da célula. */
    x: z.number().min(-50).max(150),
    /** O centro, em % da altura da célula. */
    y: z.number().min(-50).max(150),
    /** Graus, no sentido horário. */
    rotacao: z.number().min(-180).max(180),
};
/** Figurinha ou logo da biblioteca: a altura sai da proporção da imagem, a largura é a escolha do operador. */
export const camadaDeImagemSchema = z.object({
    ...baseDaCamada,
    tipo: z.literal("imagem"),
    figurinhaId: z.number().int().positive(),
    /** Em % da largura da célula. */
    largura: z.number().min(2).max(200),
});
export const camadaDeTextoSchema = z.object({
    ...baseDaCamada,
    tipo: z.literal("texto"),
    texto: z
        .string()
        .min(1)
        .max(MAX_LINHAS_DO_TEXTO * MAX_CARACTERES_POR_LINHA + (MAX_LINHAS_DO_TEXTO - 1))
        .refine((t) => t.split("\n").length <= MAX_LINHAS_DO_TEXTO &&
        t.split("\n").every((l) => l.length <= MAX_CARACTERES_POR_LINHA), `No máximo ${MAX_LINHAS_DO_TEXTO} linhas de ${MAX_CARACTERES_POR_LINHA} letras`)
        .refine((t) => t.trim() !== "", "O texto não pode ficar em branco"),
    /** Uma das fontes do pacote (`FONTES_DE_TEXTO`): o id desconhecido é recusado já no pedido. */
    fonteId: z.string().min(1).max(40).refine(ehFonteDeTexto, "Escolha uma das fontes da lista"),
    /** O tamanho da letra, em % da largura da célula. */
    corpo: z.number().min(1).max(60),
    cor,
    /** Contorno da letra; `espessura` é fração do corpo. */
    contorno: z.object({ cor, espessura: z.number().min(0).max(0.25) }).optional(),
    alinhamento: z.enum(ALINHAMENTOS_DO_TEXTO),
    /** O eixo de peso da fonte (100 a 900); as fontes de peso único ignoram. */
    peso: z.number().int().min(100).max(900),
    italico: z.boolean(),
    /** Altura da linha, em múltiplos do corpo. */
    entrelinha: z.number().min(0.8).max(2),
});
export const camadaSchema = z.discriminatedUnion("tipo", [
    camadaDeImagemSchema,
    camadaDeTextoSchema,
]);
/** As camadas de uma célula, na ordem de baixo para cima. */
export const camadasSchema = z.array(camadaSchema).max(MAX_CAMADAS_POR_CELULA);
//# sourceMappingURL=camadas.js.map